<template>
    <div class="container">
        <v-title :breadcrumb="breadcrumb">
            {{pageTitle}}
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'integration-view'}"></v-cancel>
                <r-button class="inline-s" @click="openPreviewWebhook" :disabled="isLoading">Test Integration</r-button>
                <r-button type="primary" @click="save" :disabled="isLoading">Save</r-button>
            </div>
        </v-title>
        <v-error-messages :has-frontend-errors="$v.$error" class="stack-l">
            <li class="list-item" v-if="!$v.googleSheetId.required">Google Sheet ID is required</li>
            <li class="list-item" v-if="!$v.eventType.required">EventFilter is required</li>
            <li class="list-item" v-if="!$v.selectedEventFields.required">Requires at least 1 field</li>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Google Spreadsheet</h2>
            <div slot="description">
                <p class="stack-m">Choose your spreadsheet.</p>
            </div>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item" v-if="credentialHash">
                            <p class="stack-s">Select Google Sheet</p>
                            <div class="grid-content">
                                <div class="grid-item grid-item-full">
                                    <v-select
                                        v-model="googleSheetId"
                                        :validate="$v.googleSheetId"
                                        :async-find="asyncFindCredentialItems"
                                        :async-get-init-value="asyncFindCredentialItems"
                                        help-text="Search for your Google Sheet (starting with the beginning of the file name)"
                                    />
                                </div>
                            </div>
                            <div class="divider has-or">&nbsp;&nbsp;&nbsp;OR&nbsp;&nbsp;&nbsp;</div>
                            <p class="stack-s">Google Sheet ID</p>
                            <div class="grid-content">
                                <div class="grid-item grid-item-full">
                                    <r-input
                                        type="text"
                                        v-model="googleSheetId"
                                        :validate="$v.googleSheetId"
                                        @blur="extractIdFromUrl"
                                        help-text="Just copy the full Google Sheet url and paste it into the field."
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="grid-item" v-else>
                            <p class="text-muted stack-m">Connect to your Google account to continue.</p>
                            <r-button class="inline-s" type="primary" @click="createOAuth2Credentials" :loading="isLoading">
                                Connect to Google Sheets
                            </r-button>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Event Filter</h2>
            <div slot="description">
                <p class="stack-m">You can subscribe to a specific event.</p>
            </div>
            <div slot="content" class="tile position-relative">
                <div class="tile-content">
                    <v-inline-loader v-if="isUpdatingRules"/>
                    <div class="grid-content">
                        <div class="grid-item">
                            <p class="stack-s">Select Event</p>
                            <div class="grid-content">
                                <div class="grid-item grid-item-full">
                                    <v-select
                                        v-model="eventType"
                                        :validate="$v.eventType"
                                        :options="systemEventsOptions"
                                        @input="changeEventFilter"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="eventType" :key="eventType">
                    <condition-builder
                        inline
                        :criteria="eventCriteria"
                        :event="selectedEvent"
                        @update="updateCriteria"
                    />
                    <div class="tile-content">
                        <div class="grid-item">
                            <p class="stack-s">Fields</p>
                            <p class="stack-s text-muted">Drag and drop the fields to change the order of you document</p>
                            <div class="grid-content">
                                <div class="grid-item grid-item-full">
                                    <fields-select
                                        :available-fields="eventFields"
                                        :fields="selectedEventFields"
                                        @update="updateSelectedEventFields"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'integrations-google-sheets'}"></v-cancel>
                <r-button class="inline-s" @click="openPreviewWebhook" :disabled="isLoading">Test Integration</r-button>
                <r-button type="primary" @click="save" :disabled="isLoading">Save</r-button>
            </div>
        </div>
        <preview-webhook-view
            v-if="isPreviewWebhookOpened"
            :is-loading="isLoading"
            :webhook="webhook"
            v-on:cancel="hidePreviewWebhook"
            v-on:test-webhook="testWebhook"/>
    </div>
</template>

<script>
    import compare from '@/common/helpers/compare';
    import EventModel from '@/common/models/rules/event-model';
    import vTitle from '@/components/v-title';
    import {
        vCancel, vErrorMessages, vFormTile, vSelect,
    } from '@/components/form';
    import ConditionBuilder from '@/modules/rules-engine/components/condition-builder/condition-builder';
    import vInlineLoader from '@/components/v-inline-loader';
    import FieldsSelect from '../components/fields-select';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import {required} from 'vuelidate/lib/validators';
    import PlaceholderIndexer from '@/common/spec/placeholder-indexer';
    import RuleSetModel from '@/common/models/rules/rule-set-model';
    import IntegrationModel from '@/common/models/integration-model';
    import IntegrationsConfigurationsModel from '@/common/models/integrations-configurations-model';
    import CredentialItemModel from '@/common/models/credential-item-model';
    import ConnectionWindow from '@/modules/integrations/connection-window';
    import {
        parseRuleSet,
        buildWebhook,
        buildBind,
        removeIntegration,
        insertIntegration,
    } from '@/modules/integrations/google-sheets/google-sheets-handlers';

    // noinspection JSUnusedGlobalSymbols
    export default {
        mixins: [FormLoaderMixin],
        components: {
            vTitle,
            vFormTile,
            vCancel,
            vSelect,
            vErrorMessages,
            vInlineLoader,
            FieldsSelect,
            ConditionBuilder,
            PreviewWebhookView: () => import('@/components/webhook/preview-webhook-view'),
        },
        props: {
            integration: {
                type: IntegrationModel,
                required: true,
            },
            configuration: {
                type: IntegrationsConfigurationsModel,
            },
        },
        validations: {
            googleSheetId: {required},
            eventType: {required},
            selectedEventFields: {required},
        },
        data() {
            return {
                isUpdatingRules: false,
                systemEvents: {},
                credentialHash: null,
                credentialItems: [],
                googleSheetId: null,
                googleSheetName: null,
                eventType: null,
                eventCriteria: null,
                selectedEventFields: [],
                connectionWindow: new ConnectionWindow({window}),
                originalEvent: null,
                originalCriteria: null,
                isPreviewWebhookOpened: false,
            };
        },
        watch: {
            googleSheetId(newGoogleSheetId) {
                this.extractIdFromUrl(newGoogleSheetId);
            },
        },
        computed: {
            pageTitle() {
                return `${this.isFormUpdating ? `Edit` : `Set Up`} Google Sheet Spreadsheet`;
            },
            breadcrumb() {
                const route = this.isFormUpdating
                    ? {name: 'integration-edit', params: {service: this.integration.service, id: this.id}}
                    : {name: 'integration-add', params: {service: this.integration.service}};

                return {label: this.pageTitle, route};
            },
            systemEventsOptions() {
                return Object.values(this.systemEvents).map(({title: label, eventType: value}) => ({label, value}));
            },
            selectedEvent() {
                return this.eventType
                    ? this.systemEvents[this.eventType]
                    : null;
            },
            eventFields() {
                if (!this.selectedEvent) {
                    return [];
                }

                const indexer = new PlaceholderIndexer(
                    this.$store.getters['rulesEngine/customFields'],
                    this.selectedEvent.placeholderPathways[0].pathways || null,
                );

                return Object.values(indexer.index || {}).map(
                    ({
                        fullPath,
                        lineage,
                        field,
                    }) => ({
                        value: fullPath,
                        lineage: lineage.slice(1),
                        description: field.description,
                    }),
                );
            },
            webhook() {
                return buildWebhook(this);
            },
        },
        methods: {
            async fetchData() {
                this.systemEvents = await this.fetchSystemEvents();

                if (this.isFormUpdating) {
                    try {
                        // Set `eventType` before the call of the `eventFields`.
                        this.eventType = this.configuration.eventType;
                        // cache original event type for restoration condition check
                        this.originalEvent = this.eventType;

                        const ruleSet = await this.fetchRuleSet(this.eventType);
                        const {
                            credentialHash,
                            googleSheetId,
                            googleSheetName,
                            eventCriteria,
                            selectedFields,
                        } = parseRuleSet(
                            ruleSet,
                            this.eventFields,
                        );

                        this.credentialHash = credentialHash;
                        this.googleSheetId = googleSheetId;
                        this.googleSheetName = googleSheetName;
                        this.updateSelectedEventFields(selectedFields);
                        this.updateCriteria(eventCriteria);
                        // cache original criteria for future restoration
                        this.originalCriteria = this.eventCriteria;
                    } catch (error) {
                        this.$toast.fromError(error);
                    }
                }
            },
            async fetchSystemEvents() {
                try {
                    const {items} = await this.$api(({events}) => events.getAll());
                    // TODO: Copy-paste from system-events.vue, consider to remove code duplications.
                    return items
                        .map(item => new EventModel(item.fields))
                        // Alphabetical sort by event type
                        .sort((a, b) => compare.values(a.eventType, b.eventType))
                        // Indexing by event type, see `eventType` setter.
                        .reduce((result, item) => {
                            result[item.eventType] = item;
                            return result;
                        }, {});
                } catch (error) {
                    this.$toast.fromError(error);
                    return {};
                }
            },
            async fetchRuleSet(eventType) {
                const {fields: ruleSet} = await this.$api(({events}) => events.getRules({eventType}));
                return new RuleSetModel(ruleSet);
            },
            async changeEventFilter(eventType) {
                this.isUpdatingRules = true;
                try {
                    this.eventType = eventType;
                    this.updateSelectedEventFields([...this.eventFields].splice(0, 5));
                    // update criteria with saved restore for original event or null for a new selection
                    this.updateCriteria(this.originalEvent === this.eventType ? this.originalCriteria : null);
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isUpdatingRules = false;
                }
            },
            async asyncFindCredentialItems(q = null, params = {}) {
                if (!this.credentialHash) {
                    return [];
                }

                const credentialHashesItemsParams = {
                    hash: this.credentialHash,
                    q,
                    params,
                };
                const {items} = await this.$api(
                    ({credentialHashes}) => credentialHashes.getOAuth2CredentialItems(credentialHashesItemsParams),
                );

                this.credentialItems = items.map(({fields}) => (new CredentialItemModel(fields)).toSelectOption());

                return this.credentialItems;
            },
            extractIdFromUrl(input) {
                const matches = input.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)\//);
                if (matches) {
                    const [, googleSheetId] = matches;
                    this.$nextTick(() => {
                        this.googleSheetId = googleSheetId;
                        this.googleSheetName = this.findGoogleSheetNameById();
                    });
                }
            },
            findGoogleSheetNameById() {
                if (this.credentialItems.length === 0) {
                    return this.googleSheetId;
                }

                const item = this.credentialItems.find(({value}) => value === this.googleSheetId);

                return item
                    ? item.label
                    : this.googleSheetId;
            },
            updateSelectedEventFields(fields) {
                this.selectedEventFields = fields;
            },
            updateCriteria(criteria) {
                if (this.eventType === this.originalEvent) {
                    // update original criteria to allow to restore from changes
                    this.originalCriteria = criteria;
                }
                this.eventCriteria = criteria;
            },
            async testWebhook({url, body}) {
                this.isLoading = true;
                this.$v.$touch();
                if (!this.$v.$error) {
                    try {
                        const webhook = this.webhook.getPayload();
                        webhook.url = url;
                        webhook.body = body;
                        await this.$api(({previews}) => previews.triggerWebhookRuleAction({data: webhook}));
                        this.$toast.positive('Google Sheet integration successfully tested');
                    } catch (error) {
                        this.$toast.fromError(error);
                    }
                }
                this.isLoading = false;
            },
            async save() {
                this.isLoading = true;
                this.$v.$touch();
                if (!this.$v.$error) {
                    try {
                        // Sync sheet name build bind
                        this.googleSheetName = this.findGoogleSheetNameById();
                        const bind = buildBind(this);
                        const ruleSet = await this.fetchRuleSet(this.eventType);

                        if (this.isFormUpdating) {
                            // If event type was changed, we should remove bind from old rule set.
                            if (this.configuration.eventType !== this.eventType) {
                                const changedRuleSet = await this.fetchRuleSet(this.configuration.eventType);
                                removeIntegration(changedRuleSet, this.configuration);
                                await this.$api(
                                    ({events}) => events.updateRules({
                                        eventType: this.configuration.eventType,
                                        data: changedRuleSet.getPayload(),
                                    }),
                                );
                            }

                            removeIntegration(ruleSet, this.configuration);
                            insertIntegration(ruleSet, bind);
                        } else {
                            insertIntegration(ruleSet, bind);
                        }

                        await this.$api(
                            ({events}) => events.updateRules({
                                eventType: this.eventType,
                                data: ruleSet.getPayload(),
                            }),
                        );

                        this.$router.push({name: 'integration-view'});
                    } catch (error) {
                        this.$toast.fromError(error);
                    }
                }
                this.isLoading = false;
            },
            async createOAuth2Credentials() {
                this.isLoading = true;

                // To debug the callback from Google, just open callback URL in the popup with needed query params.
                // const {href: oauth2ConnectUrl} = this.$router.resolve({
                //     name: 'integration-callback',
                //     params: {
                //         service: this.integration.service,
                //     },
                //     query: {
                //         code: 'foobar',
                //         scope: 'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/spreadsheets',
                //     },
                // });
                const {href: oauth2ConnectUrl} = this.integration.oauth2ConnectUrl;
                await this.connectionWindow
                    .sendRequest({url: oauth2ConnectUrl})
                    .then(async ({scope, code}) => {
                        const data = {
                            service: this.integration.service,
                            scopes: [...scope.split(' ')],
                            code,
                        };
                        // Mock credential, to debug the component, w/o valid code.
                        // this.credentialHash = {...data}.hash || data.code;
                        const {fields} = await this.$api(
                            ({credentialHashes}) => credentialHashes.createOAuth2Credential({data}),
                        );
                        this.credentialHash = fields.hash;
                    })
                    .catch((error) => {
                        this.$toast.fromError(error);
                    })
                    .finally(this.stopLoadingPage);
            },
            openPreviewWebhook() {
                this.$v.$touch();
                if (!this.$v.$error) {
                    this.isPreviewWebhookOpened = true;
                }
            },
            hidePreviewWebhook() {
                this.isPreviewWebhookOpened = false;
            },
        },
        async mounted() {
            // For form updating it also fetches data, but by mixin, there is we avoid double fetching.
            if (!this.isFormUpdating) {
                this.startLoadingPage();
                try {
                    await this.fetchData();
                } finally {
                    this.stopLoadingPage();
                }
            }
        },
        beforeDestroy() {
            this.connectionWindow.close();
        },
    };
</script>
